import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { SectionConfigAdmin, SectionConfigCreateRequest } from '@vedjegy/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SubRow = {
  id: string;
  subcritId: string;
  label: string;
  maxScore: number;
  sortOrder: number;
  draft: string;   // edit buffer inside the panel
};

type CriterionData = {
  criterionRowId: string;
  maxScore: number;
  subcriteria: SubRow[];
};

/** Full row data — used for both the list and the edit panel */
type SectionRow = {
  dbId: string;
  id: string;
  year: number;
  title: string;
  minChars: string;
  maxChars: string;
  maxScore: string;
  mandatoryForNew: boolean;
  mandatoryForRenewal: boolean;
  displayOrder: number;
  formType: 'sole_trader' | 'company' | null;
  rubricType: 'standard' | 'simple';
  questionText: string;
  suggestedAttachments: string;
  criterionData: CriterionData | null;
};

function toRow(s: SectionConfigAdmin, criterionData: CriterionData | null): SectionRow {
  return {
    dbId:                 s.dbId,
    id:                   s.id,
    year:                 s.year,
    title:                s.title,
    minChars:             s.minChars != null ? String(s.minChars) : '',
    maxChars:             s.maxChars != null ? String(s.maxChars) : '',
    maxScore:             String(s.maxScore),
    mandatoryForNew:      s.mandatoryForNew,
    mandatoryForRenewal:  s.mandatoryForRenewal,
    displayOrder:         s.displayOrder,
    formType:             s.formType,
    rubricType:           s.rubricType ?? 'standard',
    questionText:         s.questionText ?? '',
    suggestedAttachments: s.suggestedAttachments ?? '',
    criterionData,
  };
}

const YEAR_OPTIONS = [2027, 2026, 2025];

const FORM_TYPE_LABEL: Record<string, string> = {
  sole_trader: 'Egyéni',
  company:     'Társaság',
};

// ---------------------------------------------------------------------------
// Slide-over panel
// ---------------------------------------------------------------------------

interface PanelProps {
  row: SectionRow;
  saving: boolean;
  onClose: () => void;
  onSave: (updated: SectionRow) => void;
}

function EditPanel({ row, saving, onClose, onSave }: PanelProps) {
  const [draft, setDraft] = useState<SectionRow>(() => ({
    ...row,
    criterionData: row.criterionData
      ? {
          ...row.criterionData,
          subcriteria: row.criterionData.subcriteria.map((s) => ({
            ...s,
            draft: String(s.maxScore),
          })),
        }
      : null,
  }));

  function field(key: keyof SectionRow, value: unknown) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function subDraft(idx: number, value: string) {
    if (!draft.criterionData) return;
    setDraft((prev) => ({
      ...prev,
      criterionData: {
        ...prev.criterionData!,
        subcriteria: prev.criterionData!.subcriteria.map((s, i) =>
          i === idx ? { ...s, draft: value } : s,
        ),
      },
    }));
  }

  const inp: React.CSSProperties = {
    width: '100%', padding: '7px 10px', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block',
  };
  const group: React.CSSProperties = { marginBottom: 14 };

  const subTotal = draft.criterionData?.subcriteria.reduce(
    (s, sub) => s + (parseInt(sub.draft, 10) || 0), 0,
  ) ?? 0;
  const subMismatch = draft.rubricType === 'standard' && draft.criterionData &&
    subTotal > 0 && subTotal !== (parseInt(draft.maxScore, 10) || 0);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)',
          zIndex: 40,
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 520,
        background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,.15)',
        zIndex: 50, display: 'flex', flexDirection: 'column',
        overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <div>
            <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace', marginBottom: 2 }}>{draft.id}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>{draft.title || 'Szekció szerkesztése'}</div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 20, cursor: 'pointer',
            color: '#9ca3af', lineHeight: 1, padding: '4px 8px',
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', flex: 1 }}>

          {/* Cím */}
          <div style={group}>
            <label style={lbl}>Cím</label>
            <input style={inp} value={draft.title}
              onChange={(e) => field('title', e.target.value)} />
          </div>

          {/* Kérdésszöveg */}
          <div style={group}>
            <label style={lbl}>Kérdésszöveg <span style={{ fontWeight: 400, color: '#9ca3af' }}>(pályázónak megjelenő)</span></label>
            <textarea spellCheck lang="hu"
              style={{ ...inp, minHeight: 100, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
              value={draft.questionText}
              placeholder="Írja le a pályázótól elvárt választ…"
              onChange={(e) => field('questionText', e.target.value)}
            />
          </div>

          {/* Csatolmány tipp */}
          <div style={group}>
            <label style={lbl}>Javasolt csatolmányok <span style={{ fontWeight: 400, color: '#9ca3af' }}>(tipp)</span></label>
            <textarea spellCheck lang="hu"
              style={{ ...inp, minHeight: 60, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
              value={draft.suggestedAttachments}
              placeholder="pl. Képzési igazolások, tanúsítványok"
              onChange={(e) => field('suggestedAttachments', e.target.value)}
            />
          </div>

          {/* Karakterhatárok + Max pont */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Min. karakter</label>
              <input style={inp} type="number" min={0} value={draft.minChars}
                onChange={(e) => field('minChars', e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Max. karakter</label>
              <input style={inp} type="number" min={0} value={draft.maxChars}
                onChange={(e) => field('maxChars', e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Max. pont</label>
              <input style={inp} type="number" min={0} value={draft.maxScore}
                onChange={(e) => field('maxScore', e.target.value)} />
            </div>
          </div>

          {/* Pontozás típus + Form típus */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Pontozás típusa</label>
              <select style={inp} value={draft.rubricType}
                onChange={(e) => field('rubricType', e.target.value as 'standard' | 'simple')}>
                <option value="standard">Rubric (4 al-szempont)</option>
                <option value="simple">Egyszerű</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Pályázó típusa</label>
              <select style={inp} value={draft.formType ?? ''}
                onChange={(e) => field('formType', (e.target.value as 'sole_trader' | 'company') || null)}>
                <option value="">Mindkettő</option>
                <option value="sole_trader">Egyéni vállalkozó</option>
                <option value="company">Társaság</option>
              </select>
            </div>
          </div>

          {/* Kötelező checkboxok */}
          <div style={{ display: 'flex', gap: 24, marginBottom: 18 }}>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
              <input type="checkbox" checked={draft.mandatoryForNew}
                onChange={(e) => field('mandatoryForNew', e.target.checked)} />
              Kötelező (új pályázat)
            </label>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
              <input type="checkbox" checked={draft.mandatoryForRenewal}
                onChange={(e) => field('mandatoryForRenewal', e.target.checked)} />
              Kötelező (megújítás)
            </label>
          </div>

          {/* Rubric al-szempontok */}
          {draft.rubricType === 'standard' && draft.criterionData && draft.criterionData.subcriteria.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{
                fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span>Al-szempontok max. pontjai</span>
                {subMismatch && (
                  <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>
                    ⚠ összeg: {subTotal} ≠ {parseInt(draft.maxScore, 10) || 0}
                  </span>
                )}
              </div>
              <div style={{
                border: '1px solid #dbeafe', borderRadius: 8, overflow: 'hidden',
                background: '#f8faff',
              }}>
                {draft.criterionData.subcriteria.map((sub, si) => (
                  <div key={sub.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 14px',
                    borderBottom: si < draft.criterionData!.subcriteria.length - 1
                      ? '1px solid #dbeafe' : 'none',
                  }}>
                    <div>
                      <div style={{ fontSize: 13, color: '#1e3a8a', fontWeight: 600 }}>{sub.label}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{sub.subcritId}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="number" min={0} max={999}
                        value={sub.draft}
                        onChange={(e) => subDraft(si, e.target.value)}
                        style={{
                          width: 64, padding: '5px 8px', textAlign: 'center',
                          border: '2px solid ' + (sub.draft !== String(sub.maxScore) ? '#f59e0b' : '#bfdbfe'),
                          borderRadius: 6, fontSize: 14, fontWeight: 700, color: '#1e40af',
                        }}
                      />
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>pt</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e5e7eb',
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          position: 'sticky', bottom: 0, background: '#fff',
        }}>
          <button onClick={onClose} disabled={saving} style={{
            padding: '8px 20px', background: '#f3f4f6', color: '#374151',
            border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600,
            fontSize: 13, cursor: 'pointer',
          }}>
            Mégse
          </button>
          <button onClick={() => onSave(draft)} disabled={saving} style={{
            padding: '8px 24px', background: '#0ea5e9', color: '#fff',
            border: 'none', borderRadius: 6, fontWeight: 700,
            fontSize: 13, cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}>
            {saving ? 'Mentés…' : 'Mentés'}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function AdminSectionConfigPage() {
  const [selectedYear, setSelectedYear] = useState<number>(2027);
  const [rows, setRows]         = useState<SectionRow[]>([]);
  const [loading, setLoading]   = useState(true);
  const [globalError, setGlobalError] = useState('');

  const [editingRow, setEditingRow]   = useState<SectionRow | null>(null);
  const [panelSaving, setPanelSaving] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newRow, setNewRow] = useState<SectionConfigCreateRequest>({
    sectionId: '', title: '', maxScore: 0,
    mandatoryForNew: true, mandatoryForRenewal: true,
    rubricType: 'standard',
  });
  const [addError, setAddError]   = useState('');
  const [addSaving, setAddSaving] = useState(false);

  const load = useCallback(async (year: number) => {
    setLoading(true);
    try {
      const [sectionData, criteriaData] = await Promise.all([
        api.sectionConfig.list(year),
        api.reviewCriteria.list(year).catch(() => null),
      ]);

      const criterionMap = new Map<string, CriterionData>();
      if (criteriaData) {
        for (const c of criteriaData.criteria) {
          criterionMap.set(c.criterionId, {
            criterionRowId: c.id,
            maxScore:       c.maxScore,
            subcriteria:    c.subcriteria.map((s) => ({ ...s, draft: String(s.maxScore) })),
          });
        }
      }

      setRows(sectionData.sections.map((s) => toRow(s, criterionMap.get(s.id) ?? null)));
    } catch {
      setGlobalError('Nem sikerült betölteni a szekciókat.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(selectedYear); }, [load, selectedYear]);

  // ---- save from panel ----
  async function handlePanelSave(updated: SectionRow) {
    setPanelSaving(true);
    try {
      // 1. Save section config
      await api.sectionConfig.update(updated.dbId, {
        title:                updated.title,
        minChars:             updated.minChars !== '' ? parseInt(updated.minChars, 10) : null,
        maxChars:             updated.maxChars !== '' ? parseInt(updated.maxChars, 10) : null,
        maxScore:             parseInt(updated.maxScore, 10) || 0,
        mandatoryForNew:      updated.mandatoryForNew,
        mandatoryForRenewal:  updated.mandatoryForRenewal,
        formType:             updated.formType,
        rubricType:           updated.rubricType,
        questionText:         updated.questionText || null,
        suggestedAttachments: updated.suggestedAttachments || null,
      });

      // 2. Sync max_score to review_criteria
      if (updated.criterionData) {
        await api.reviewCriteria.update(updated.criterionData.criterionRowId, {
          maxScore: parseInt(updated.maxScore, 10) || 0,
        }).catch(() => {});

        // 3. Save changed sub-criteria
        const origSubs = rows.find((r) => r.dbId === updated.dbId)?.criterionData?.subcriteria ?? [];
        await Promise.all(
          updated.criterionData.subcriteria.map((sub, i) => {
            const parsed = parseInt(sub.draft, 10) || 0;
            if (parsed === origSubs[i]?.maxScore) return Promise.resolve();
            return api.reviewCriteria.updateSubcriteria(sub.id, { maxScore: parsed }).catch(() => {});
          }),
        );
      }

      // 4. Reflect changes in list
      setRows((prev) => prev.map((r) => {
        if (r.dbId !== updated.dbId) return r;
        return {
          ...updated,
          criterionData: updated.criterionData
            ? {
                ...updated.criterionData,
                subcriteria: updated.criterionData.subcriteria.map((s) => ({
                  ...s,
                  maxScore: parseInt(s.draft, 10) || 0,
                })),
              }
            : null,
        };
      }));

      setEditingRow(null);
    } catch {
      setGlobalError('Mentés sikertelen: ' + updated.id);
    } finally {
      setPanelSaving(false);
    }
  }

  // ---- reorder ----
  async function moveRow(dbId: string, direction: 'up' | 'down') {
    const idx = rows.findIndex((r) => r.dbId === dbId);
    if (idx < 0) return;
    const newRows = [...rows];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= newRows.length) return;
    const tmpOrder = newRows[idx].displayOrder;
    newRows[idx]     = { ...newRows[idx],    displayOrder: newRows[swapIdx].displayOrder };
    newRows[swapIdx] = { ...newRows[swapIdx], displayOrder: tmpOrder };
    newRows.sort((a, b) => a.displayOrder - b.displayOrder);
    setRows(newRows);
    try {
      await api.sectionConfig.reorder(newRows.map((r) => ({ dbId: r.dbId, displayOrder: r.displayOrder })));
    } catch {
      setGlobalError('Sorrend mentése sikertelen.');
      void load(selectedYear);
    }
  }

  // ---- delete ----
  async function deleteRow(row: SectionRow) {
    if (!confirm(`Törli a(z) "${row.id} – ${row.title}" szekciót? Ez visszavonhatatlan.`)) return;
    try {
      await api.sectionConfig.delete(row.dbId);
      setRows((prev) => prev.filter((r) => r.dbId !== row.dbId));
    } catch {
      setGlobalError('Törlés sikertelen.');
    }
  }

  // ---- add new ----
  async function handleAdd() {
    setAddError('');
    if (!newRow.sectionId.trim() || !newRow.title.trim()) {
      setAddError('Szekció ID és cím kötelező.'); return;
    }
    setAddSaving(true);
    try {
      await api.sectionConfig.create({ ...newRow, year: selectedYear });
      setShowAddForm(false);
      setNewRow({ sectionId: '', title: '', maxScore: 0, mandatoryForNew: true, mandatoryForRenewal: true, rubricType: 'standard' });
      await load(selectedYear);
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'Hiba';
      setAddError(msg.includes('409') ? 'Ez a szekció ID már létezik.' : 'Hozzáadás sikertelen.');
    } finally {
      setAddSaving(false);
    }
  }

  const inp: React.CSSProperties = {
    padding: '5px 8px', border: '1px solid #d1d5db',
    borderRadius: 5, fontSize: 13, boxSizing: 'border-box',
  };

  return (
    <AdminLayout>
      <div style={{ padding: 28, maxWidth: 2000 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Form szekció konfiguráció</h1>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>Év:</span>
              <select value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                style={{ ...inp, padding: '6px 10px' }}>
                {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button
              onClick={() => { setShowAddForm((v) => !v); setAddError(''); }}
              style={{ padding: '8px 20px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              {showAddForm ? 'Mégse' : '+ Új szekció'}
            </button>
          </div>
        </div>

        {globalError && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: 13 }}>
            {globalError}
            <button onClick={() => setGlobalError('')} style={{ marginLeft: 10, fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
          </div>
        )}

        {/* Add form */}
        {showAddForm && (
          <div style={{ marginBottom: 20, padding: 16, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Új szekció ({selectedYear})</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 3 }}>Szekció ID *</div>
                <input style={{ ...inp, width: 90 }} placeholder="pl. 3.11" value={newRow.sectionId}
                  onChange={(e) => setNewRow((p) => ({ ...p, sectionId: e.target.value }))} />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 3 }}>Cím *</div>
                <input style={{ ...inp, width: '100%' }} placeholder="Szekció neve" value={newRow.title}
                  onChange={(e) => setNewRow((p) => ({ ...p, title: e.target.value }))} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 3 }}>Pontozás</div>
                <select style={{ ...inp, width: 140 }} value={newRow.rubricType ?? 'standard'}
                  onChange={(e) => setNewRow((p) => ({ ...p, rubricType: e.target.value as 'standard' | 'simple' }))}>
                  <option value="standard">Rubric</option>
                  <option value="simple">Egyszerű</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 3 }}>Max. pont</div>
                <input style={{ ...inp, width: 70 }} type="number" min={0} value={newRow.maxScore ?? 0}
                  onChange={(e) => setNewRow((p) => ({ ...p, maxScore: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 3 }}>Típus</div>
                <select style={{ ...inp, width: 110 }} value={newRow.formType ?? ''}
                  onChange={(e) => setNewRow((p) => ({ ...p, formType: (e.target.value as 'sole_trader' | 'company') || null }))}>
                  <option value="">Mindkettő</option>
                  <option value="sole_trader">Egyéni</option>
                  <option value="company">Társaság</option>
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={newRow.mandatoryForNew ?? true}
                    onChange={(e) => setNewRow((p) => ({ ...p, mandatoryForNew: e.target.checked }))} />
                  Köt. (új)
                </label>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={newRow.mandatoryForRenewal ?? true}
                    onChange={(e) => setNewRow((p) => ({ ...p, mandatoryForRenewal: e.target.checked }))} />
                  Köt. (megúj.)
                </label>
              </div>
              <button onClick={() => void handleAdd()} disabled={addSaving}
                style={{ padding: '8px 20px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer', alignSelf: 'flex-end' }}>
                {addSaving ? 'Mentés…' : 'Hozzáadás'}
              </button>
            </div>
            {addError && <div style={{ marginTop: 8, fontSize: 13, color: '#dc2626' }}>{addError}</div>}
          </div>
        )}

        {/* Compact list */}
        {loading ? (
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Betöltés…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Nincs szekció konfigurálva a(z) {selectedYear}. évre.</div>
        ) : (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
            {rows.map((row, idx) => (
              <div key={row.dbId} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '11px 16px',
                borderBottom: idx < rows.length - 1 ? '1px solid #f3f4f6' : 'none',
                background: '#fff',
                transition: 'background .1s',
              }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
              >
                {/* Sorrend */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                  <button onClick={() => void moveRow(row.dbId, 'up')} disabled={idx === 0}
                    style={{ padding: '1px 5px', fontSize: 10, border: '1px solid #e5e7eb', borderRadius: 3, cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.25 : 0.7, background: '#f9fafb', lineHeight: 1.2 }}>▲</button>
                  <button onClick={() => void moveRow(row.dbId, 'down')} disabled={idx === rows.length - 1}
                    style={{ padding: '1px 5px', fontSize: 10, border: '1px solid #e5e7eb', borderRadius: 3, cursor: idx === rows.length - 1 ? 'default' : 'pointer', opacity: idx === rows.length - 1 ? 0.25 : 0.7, background: '#f9fafb', lineHeight: 1.2 }}>▼</button>
                </div>

                {/* ID badge */}
                <div style={{
                  fontFamily: 'monospace', fontSize: 12, fontWeight: 700,
                  color: '#6b7280', minWidth: 36, flexShrink: 0,
                }}>{row.id}</div>

                {/* Cím */}
                <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#111827', minWidth: 0 }}>
                  {row.title}
                  {row.formType && (
                    <span style={{ marginLeft: 7, fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>
                      ({FORM_TYPE_LABEL[row.formType]})
                    </span>
                  )}
                </div>

                {/* Pontozás badge */}
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, flexShrink: 0,
                  background: row.rubricType === 'standard' ? '#dbeafe' : '#fef3c7',
                  color:      row.rubricType === 'standard' ? '#1e40af' : '#92400e',
                }}>
                  {row.rubricType === 'standard' ? 'Rubric' : 'Egyszerű'}
                </span>

                {/* Max pont */}
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', flexShrink: 0, minWidth: 48, textAlign: 'right' }}>
                  {row.maxScore} <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>pt</span>
                </div>

                {/* Kötelező jelzők */}
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <span title="Kötelező új pályázatnál" style={{
                    fontSize: 10, padding: '2px 6px', borderRadius: 4,
                    background: row.mandatoryForNew ? '#dcfce7' : '#f3f4f6',
                    color:      row.mandatoryForNew ? '#15803d' : '#9ca3af',
                    fontWeight: 600,
                  }}>Új</span>
                  <span title="Kötelező megújításnál" style={{
                    fontSize: 10, padding: '2px 6px', borderRadius: 4,
                    background: row.mandatoryForRenewal ? '#dcfce7' : '#f3f4f6',
                    color:      row.mandatoryForRenewal ? '#15803d' : '#9ca3af',
                    fontWeight: 600,
                  }}>Meg.</span>
                </div>

                {/* Műveletek */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => setEditingRow(row)}
                    style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 5, cursor: 'pointer' }}
                  >
                    Szerkesztés
                  </button>
                  <button
                    onClick={() => void deleteRow(row)}
                    style={{ padding: '5px 10px', fontSize: 12, fontWeight: 600, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 5, cursor: 'pointer' }}
                  >
                    Törlés
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p style={{ marginTop: 14, fontSize: 12, color: '#9ca3af' }}>
          A módosítások azonnal érvénybe lépnek. Rubric pontozásnál a szerkesztő panelben állíthatók be az al-szempontok pontjai is.
        </p>
      </div>

      {/* Slide-over edit panel */}
      {editingRow && (
        <EditPanel
          row={editingRow}
          saving={panelSaving}
          onClose={() => setEditingRow(null)}
          onSave={(updated) => void handlePanelSave(updated)}
        />
      )}
    </AdminLayout>
  );
}
